import { supabase } from '../lib/supabase';
import { projectService } from './projectService';
import { getFinancialSummaryForProjects } from './projectCostService';
import { computeDpTotals } from '../utils/dpFinancials';

/**
 * portfolioProfitService.getPortfolio()
 *
 * Returns per-project profitability data with CORRECT numbers:
 *   - billed   = SUM(payment_values.monthly_amount) — client revenue
 *   - costs    = SUM(sub_invoice_lines.amount for non-rejected sub_invoices)
 *              + SUM(project_expenses.total_amount)
 *   - contractValue = adjustedContract from getFinancialSummaryForProjects
 *   - margin   = billed - costs
 *   - marginPct = billed > 0 ? (margin / billed) * 100 : 0
 *   - unassignedCosts = sub_invoice_lines where payment_request_id IS NULL (non-rejected)
 *                     + project_expenses where payment_request_id IS NULL
 *
 * Excludes projects with status: 'deleted', 'cancelled', 'archived'.
 * Only includes real projects (active / completed).
 *
 * @param {{ activeOnly?: boolean }} options
 */
export const getPortfolio = async ({ activeOnly = false } = {}) => {
  try {
    // 1. Fetch all visible projects — exclude deleted, cancelled, archived
    const excludeStatuses = ['deleted', 'cancelled', 'archived'];
    const allProjects = await projectService?.getAll({ excludeStatuses });

    // Additional JS-side guard: only keep truly real projects
    const realProjects = (allProjects || [])?.filter(
      (p) => !['deleted', 'cancelled', 'archived']?.includes(p?.status)
    );

    const projects = activeOnly
      ? realProjects?.filter((p) => p?.status === 'active')
      : realProjects;

    if (!projects || projects?.length === 0) {
      return { projects: [], totals: buildEmptyTotals() };
    }

    const projectIds = projects?.map((p) => p?.id)?.filter(Boolean);

    // 2. Fetch all data in bulk (parallel)
    const [
      paymentValuesResult,
      subInvoiceLinesResult,
      expensesResult,
      financialSummary,
    ] = await Promise.all([
      // Revenue: payment_values joined through payment_requests to get project_id
      supabase?.from('payment_values')?.select('monthly_amount, payment_requests!inner(project_id)')?.in('payment_requests.project_id', projectIds),

      // Sub-contractor costs: sub_invoice_lines joined through sub_invoices (non-rejected)
      supabase?.from('sub_invoice_lines')?.select('amount, sub_invoices!inner(project_id, status, payment_request_id)')?.in('sub_invoices.project_id', projectIds)?.neq('sub_invoices.status', 'rejected'),

      // Direct expenses per project (all statuses)
      supabase?.from('project_expenses')?.select('project_id, total_amount, payment_request_id')?.in('project_id', projectIds),

      // Adjusted contract value from the correct service
      getFinancialSummaryForProjects(projectIds),
    ]);

    // 3. Aggregate revenue by project_id
    const billedByProject = {};
    (paymentValuesResult?.data || [])?.forEach((pv) => {
      const pid = pv?.payment_requests?.project_id;
      if (!pid) return;
      billedByProject[pid] = (billedByProject?.[pid] || 0) + (parseFloat(pv?.monthly_amount) || 0);
    });

    // 4. Aggregate sub-invoice costs by project_id
    //    Also track unassigned (payment_request_id IS NULL)
    const subCostsByProject = {};
    const subUnassignedByProject = {};
    (subInvoiceLinesResult?.data || [])?.forEach((line) => {
      const pid = line?.sub_invoices?.project_id;
      if (!pid) return;
      const amount = parseFloat(line?.amount) || 0;
      subCostsByProject[pid] = (subCostsByProject?.[pid] || 0) + amount;
      if (!line?.sub_invoices?.payment_request_id) {
        subUnassignedByProject[pid] = (subUnassignedByProject?.[pid] || 0) + amount;
      }
    });

    // 5. Aggregate expenses by project_id
    //    Also track unassigned (payment_request_id IS NULL)
    const expensesByProject = {};
    const expenseUnassignedByProject = {};
    (expensesResult?.data || [])?.forEach((exp) => {
      const pid = exp?.project_id;
      if (!pid) return;
      const amount = parseFloat(exp?.total_amount) || 0;
      expensesByProject[pid] = (expensesByProject?.[pid] || 0) + amount;
      if (!exp?.payment_request_id) {
        expenseUnassignedByProject[pid] = (expenseUnassignedByProject?.[pid] || 0) + amount;
      }
    });

    // 6. Build per-project rows
    const projectRows = projects?.map((project) => {
      const pid = project?.id;
      const billed = billedByProject?.[pid] || 0;
      const subCosts = subCostsByProject?.[pid] || 0;
      const expenses = expensesByProject?.[pid] || 0;
      const costs = subCosts + expenses;
      const margin = billed - costs;
      const marginPct = billed > 0 ? (margin / billed) * 100 : 0;
      const unassignedCosts =
        (subUnassignedByProject?.[pid] || 0) + (expenseUnassignedByProject?.[pid] || 0);

      // contractValue: use adjustedContract from getFinancialSummaryForProjects, fall back to projects.contract_value
      const financialData = financialSummary?.[pid];
      const contractValue =
        financialData?.adjustedContract != null
          ? financialData?.adjustedContract
          : parseFloat(project?.contractValue || project?.contract_value) || 0;

      return {
        projectId: pid,
        projectNumber: project?.projectNumber || project?.project_number || '',
        name: project?.name || '',
        status: project?.status || 'active',
        contractValue,
        billed,
        costs,
        subCosts,
        expenses,
        margin,
        marginPct,
        unassignedCosts,
      };
    });

    // 7. Portfolio totals
    const totalContract = projectRows?.reduce((s, r) => s + r?.contractValue, 0);
    const totalBilled = projectRows?.reduce((s, r) => s + r?.billed, 0);
    const totalCosts = projectRows?.reduce((s, r) => s + r?.costs, 0);
    const totalMargin = totalBilled - totalCosts;
    const totalMarginPct = totalBilled > 0 ? (totalMargin / totalBilled) * 100 : 0;
    const totalUnassigned = projectRows?.reduce((s, r) => s + r?.unassignedCosts, 0);
    const projectCount = projectRows?.length;

    return {
      projects: projectRows,
      totals: {
        totalContract,
        totalBilled,
        totalCosts,
        totalMargin,
        totalMarginPct,
        totalUnassigned,
        projectCount,
      },
    };
  } catch (error) {
    console.error('portfolioProfitService.getPortfolio error:', error?.message);
    return { projects: [], totals: buildEmptyTotals() };
  }
};

const buildEmptyTotals = () => ({
  totalContract: 0,
  totalBilled: 0,
  totalCosts: 0,
  totalMargin: 0,
  totalMarginPct: 0,
  totalUnassigned: 0,
  projectCount: 0,
});

/**
 * portfolioProfitService.getProjectDetail(projectId)
 *
 * Returns a full 360° summary for a single project:
 *   - project: { id, projectNumber, name, status, pmName, supervisorName, coordinatorName }
 *   - kpis: { contrat, billed, costs, margin, marginPct }
 *   - dps: per-payment_request breakdown with billed, cost, delta, marginPct
 *   - unassignedCosts: costs not linked to any DP
 *
 * Revenue = SUM(payment_values.monthly_amount)
 * Costs   = SUM(sub_invoice_lines.amount for non-rejected sub_invoices)
 *          + SUM(project_expenses.total_amount)
 * contrat = adjustedContract from getFinancialSummaryForProjects (fallback: projects.contract_value)
 */
export const getProjectDetail = async (projectId) => {
  if (!projectId) return null;

  try {
    // 1. Fetch project info with team joins
    const { data: projectData, error: projectError } = await supabase
      ?.from('projects')
      ?.select('id, project_number, name, status, contract_value, pm:user_profiles!pm_user_id(full_name), supervisor:user_profiles!supervisor_user_id(full_name), coordinator:user_profiles!coordinator_user_id(full_name)')
      ?.eq('id', projectId)
      ?.single();

    if (projectError || !projectData) {
      console.error('getProjectDetail: project fetch error', projectError?.message);
      return null;
    }

    // 2. Fetch all payment_requests for this project (ordered by number)
    const { data: dpsData } = await supabase
      ?.from('payment_requests')
      ?.select('id, number, status, retention_rate, tps_rate, tvq_rate')
      ?.eq('project_id', projectId)
      ?.order('number', { ascending: true });

    const dps = dpsData || [];
    const dpIds = dps?.map((d) => d?.id)?.filter(Boolean);

    // 3. Bulk fetch in parallel: payment_values, sub_invoice_lines, project_expenses
    const [pvResult, silResult, expResult, financialSummary] = await Promise.all([
      // Revenue: payment_values for this project's DPs
      dpIds?.length > 0
        ? supabase?.from('payment_values')?.select('request_id, monthly_amount')?.in('request_id', dpIds)
        : Promise.resolve({ data: [] }),

      // Sub-invoice costs: lines for sub_invoices linked to this project (non-rejected)
      supabase
        ?.from('sub_invoice_lines')
        ?.select('amount, sub_invoices!inner(project_id, status, payment_request_id)')
        ?.eq('sub_invoices.project_id', projectId)
        ?.neq('sub_invoices.status', 'rejected'),

      // Project expenses for this project
      supabase
        ?.from('project_expenses')
        ?.select('total_amount, payment_request_id')
        ?.eq('project_id', projectId),

      // Adjusted contract value
      getFinancialSummaryForProjects([projectId]),
    ]);

    // 4. Aggregate revenue by dp id
    const billedByDp = {};
    (pvResult?.data || [])?.forEach((pv) => {
      const rid = pv?.request_id;
      if (!rid) return;
      billedByDp[rid] = (billedByDp?.[rid] || 0) + (parseFloat(pv?.monthly_amount) || 0);
    });

    // 5. Aggregate sub-invoice costs by payment_request_id
    const subCostByDp = {};
    let subCostUnassigned = 0;
    (silResult?.data || [])?.forEach((line) => {
      const dpId = line?.sub_invoices?.payment_request_id;
      const amount = parseFloat(line?.amount) || 0;
      if (dpId) {
        subCostByDp[dpId] = (subCostByDp?.[dpId] || 0) + amount;
      } else {
        subCostUnassigned += amount;
      }
    });

    // 6. Aggregate expenses by payment_request_id
    const expByDp = {};
    let expUnassigned = 0;
    (expResult?.data || [])?.forEach((exp) => {
      const dpId = exp?.payment_request_id;
      const amount = parseFloat(exp?.total_amount) || 0;
      if (dpId) {
        expByDp[dpId] = (expByDp?.[dpId] || 0) + amount;
      } else {
        expUnassigned += amount;
      }
    });

    // 7. Build per-DP rows (with retention fields)
    const dpRows = dps?.map((dp) => {
      const billed = billedByDp?.[dp?.id] || 0;
      const cost = (subCostByDp?.[dp?.id] || 0) + (expByDp?.[dp?.id] || 0);
      const delta = billed - cost;
      const marginPct = billed > 0 ? (delta / billed) * 100 : null;
      // Use helper defaults for TPS/TVQ — do NOT read tps_rate/tvq_rate (always NULL → NaN source)
      const { subtotal, retention: retenue, avantTaxes, totalAvecTaxes } = computeDpTotals(billed, dp?.retention_rate ?? 10);
      const retentionRate = Number.isFinite(Number(dp?.retention_rate)) ? Number(dp?.retention_rate) : 10;
      return {
        id: dp?.id,
        number: dp?.number,
        status: dp?.status,
        billed,
        cost,
        delta,
        marginPct,
        retentionRate,
        subtotal,
        retenue,
        avantTaxes,
        totalAvecTaxes,
      };
    });

    // 8. Project-level KPIs — based ONLY on DPs that have been billed (billed > 0)
    // Excludes unassigned costs and draft-DP costs from KPI; unassignedCosts returned separately.
    const facturedDPs = dpRows?.filter((d) => (d?.billed || 0) > 0);
    const totalBilled = facturedDPs?.reduce((s, d) => s + (d?.billed || 0), 0) ?? 0;
    const totalCosts = facturedDPs?.reduce((s, d) => s + (d?.cost || 0), 0) ?? 0;
    const margin = totalBilled - totalCosts;
    const marginPct = totalBilled > 0 ? (margin / totalBilled) * 100 : null;

    const financialData = financialSummary?.[projectId];
    const contrat =
      financialData?.adjustedContract != null
        ? financialData?.adjustedContract
        : parseFloat(projectData?.contract_value) || 0;

    const unassignedCosts = subCostUnassigned + expUnassigned;

    // 9. Facturation aggregates
    // enAttenteApprobation = SUM(billed) of DPs with status='submitted'
    const enAttenteApprobation = dpRows
      ?.filter((d) => d?.status === 'submitted')
      ?.reduce((s, d) => s + d?.billed, 0);

    // retenueAccumulee = SUM(retenue) of DPs with status='approved'
    const retenueAccumulee = dpRows
      ?.filter((d) => d?.status === 'approved')
      ?.reduce((s, d) => s + d?.retenue, 0);

    // 10. Fetch outstanding sub-invoices (balance > 0, status in approved_to_pay / partially_paid)
    const { data: subInvoicesRaw } = await supabase
      ?.from('sub_invoices')
      ?.select(`
        id,
        invoice_date,
        status,
        subcontractors ( id, company_name ),
        sub_invoice_lines ( amount ),
        sub_payments ( amount_paid )
      `)
      ?.eq('project_id', projectId)
      ?.in('status', ['approved_to_pay', 'partially_paid']);

    const today = new Date();
    const outstandingSubInvoices = (subInvoicesRaw || [])
      ?.map((inv) => {
        const invoiceTotal = (inv?.sub_invoice_lines || [])?.reduce(
          (s, l) => s + (parseFloat(l?.amount) || 0),
          0
        );
        const invoiceTotalWithTax = invoiceTotal * 1.14975;
        const totalPaid = (inv?.sub_payments || [])?.reduce(
          (s, p) => s + (parseFloat(p?.amount_paid) || 0),
          0
        );
        const balance = Math.max(invoiceTotalWithTax - totalPaid, 0);
        const invoiceDateStr = inv?.invoice_date;
        const ageDays = invoiceDateStr
          ? Math.floor((today - new Date(invoiceDateStr)) / (1000 * 60 * 60 * 24))
          : null;
        return {
          id: inv?.id,
          companyName: inv?.subcontractors?.company_name || '—',
          balance,
          ageDays,
          status: inv?.status,
        };
      })
      ?.filter((inv) => inv?.balance > 0);

    return {
      project: {
        id: projectData?.id,
        projectNumber: projectData?.project_number || '',
        name: projectData?.name || '',
        status: projectData?.status || '',
        pmName: projectData?.pm?.full_name || null,
        supervisorName: projectData?.supervisor?.full_name || null,
        coordinatorName: projectData?.coordinator?.full_name || null,
      },
      kpis: {
        contrat,
        billed: totalBilled,
        costs: totalCosts,
        margin,
        marginPct,
      },
      dps: dpRows,
      unassignedCosts,
      facturation: {
        enAttenteApprobation,
        retenueAccumulee,
      },
      outstandingSubInvoices,
    };
  } catch (error) {
    console.error('portfolioProfitService.getProjectDetail error:', error?.message);
    return null;
  }
};

export const portfolioProfitService = { getPortfolio, getProjectDetail };
export default { getPortfolio, getProjectDetail };
